import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, SelectsOptions, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfIntermediaryDraftDto,
  IntermediaryDraftDataRow,
  intermediaryGetById,
  intermediaryCreateChangeRequest,
  IntermediaryGetAllResponse,
  PmpIntermediaryResponse,
  ComplianceStatus,
  lendersGetLender,
  UserStatus,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';
import { CrudFormProps } from '@pmp/types';
import { mapIntermediaryToDto } from '..';
import { BrokersFormSchema } from '.';

export const BrokersCrudForm = memo(
  ({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<IntermediaryGetAllResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('broker-details');
    const [defaultComplianceStatus, setDefaultComplianceStatus] = useState<ComplianceStatus | undefined>();
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    useEffect(() => {
      lendersGetLender()
        .then(({ data }) => {
          setDefaultComplianceStatus(data?.complianceCheckConfiguration?.defaultIntermediaryStatus ?? undefined);
        })
        .catch(() => { });
    }, []);

    useEffect(() => {
      if (mode === CrudMode.EDIT && !record?.id) {
        notification().error({ title: t('global.errors.unexpected') });
        onClose();
        return;
      }
      setOpen(true);
    }, [mode, record, t, onClose]);

    useEffect(() => {
      if (!open || mode !== CrudMode.CREATE) return;
      ref.current?.form?.setFieldsValue({
        compliance: {
          complianceStatus: defaultComplianceStatus ?? ComplianceStatus.PENDING,
          onTheWatchlist: false,
        },
      });
    }, [open, mode, defaultComplianceStatus]);

    const request = useCallback(async () => {
      if (!open || mode === CrudMode.CREATE || !record?.id) return;
      setFetching(true);
      intermediaryGetById({ path: { id: record.id } })
        .then(({ data }) => {
          if (!data) return;
          const formValues = {
            ...data,
            personalData: data.personalData
              ? {
                ...data.personalData,
                birthDate: data.personalData.birthDate ? dayjs(data.personalData.birthDate) : null,
              }
              : data.personalData,
            addedToPanel: data.addedToPanel ? dayjs(data.addedToPanel) : null,
            lastUpdate: data.lastUpdate ? dayjs(data.lastUpdate) : null,
            userStatus: data.userStatus === UserStatus.ACTIVE,
          };
          ref.current?.form?.setFieldsValue(formValues);
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        })
        .finally(() => setFetching(false));
    }, [open, mode, record?.id, t]);

    useEffect(() => {
      request();
    }, [request]);

    const mapFormValuesToDraft = useCallback(
      (values: PmpIntermediaryResponse): IntermediaryDraftDataRow => mapIntermediaryToDto(values, record),
      [record],
    );

    const onConfirm: (formData: PmpIntermediaryResponse) => Promise<ValidationError[] | void> = useCallback(
      (values: PmpIntermediaryResponse) => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const data = mapFormValuesToDraft(values);
        const body: ChangeRequestOfIntermediaryDraftDto = {
          data,
          actionType: crudToActionMap[mode],
        };

        return intermediaryCreateChangeRequest({ body }).then(
          ({ data: responseData }) => {
            if (!responseData.isValid) {
              return transformCrudFormErrors({ ...responseData.errors });
            }
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes, mapFormValuesToDraft],
    );

    const setDisableSave = useCallback((value: boolean) => {
      ref.current?.setDisableSave?.(value);
    }, []);

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.brokers.labels.broker') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.brokers.labels.broker') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.brokers.labels.broker') }),
      };
      return titles[mode];
    }, [mode, t]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const crudProps: CrudProps<PmpIntermediaryResponse> = useMemo(() => {
      return {
        ref,
        open,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        mode,
        customForm: true,
        width: 1100,
        wrapperCol: { flex: 'auto' },
        disableDrawerBodyScroll: true,
        hideConfirm: activeTab === 'notes',
        onClose: handleOnClose,
        onConfirm,
      };
    }, [open, title, mode, t, handleOnClose, onConfirm, enableFourEyes, activeTab]);

    return (
      <Crud loading={fetching} {...crudProps}>
        <BrokersFormSchema form={ref.current?.form} mode={mode ?? CrudMode.VIEW} selectsOptions={selectsOptions} onTabChange={setActiveTab} defaultComplianceStatus={defaultComplianceStatus} setDisableSave={setDisableSave} />
      </Crud>
    );
  },
);
